import { Injectable } from '@angular/core';

import { AuthService } from '@app/services/auth.service';
import { Firestore } from '@angular/fire/firestore';
import {
  collection,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  query,
  where
} from 'firebase/firestore';

import { Report } from '@app/models/report';
import { User } from '@app/models/user';
import { Utility } from '@app/models/utility';
import { NgxIndexedDBService, ObjectStoreMeta } from 'ngx-indexed-db';
import { firstValueFrom } from 'rxjs';

import { RequestRecord } from '@app/models/request-record';
import { TimeStampRecord } from '@app/models/time-stamp-record';

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  constructor(
    private firestore: Firestore,
    public auth: AuthService,
    private dbService: NgxIndexedDBService
  ) { }


  loadHistories(user: User, startDate: Date, endDate: Date, withoutCache: Boolean = false) {
    return new Promise<Report>(async (resolve: (value?: Report) => void, reject: (reason?: any) => void) => {
      const key = user.uid;
      const records: Array<TimeStampRecord> = [];
      const report: Report = new Report(startDate, endDate, user.displayName, user.id, user.team);

      // ふりわけ変更影響範囲で(2日・2日+0~5時)基準日を拡げる
      const registerStartDate = new Date(startDate);
      const registerEndDate = new Date(endDate);
      registerStartDate.setDate(registerStartDate.getDate() - 2);
      const registerStartDateText = Utility.convertDateToFullDateTime(registerStartDate);
      registerEndDate.setDate(registerEndDate.getDate() + 3);
      const registerEndDateText = Utility.convertDateToFullDateTime(registerEndDate);

      // キャッシュ作成の範囲(3ヵ月+ふりわけ)
      const cacheStartDate = new Date(startDate);
      const cacheEndDate = new Date(endDate);
      cacheStartDate.setMonth(cacheStartDate.getMonth() - 2);
      cacheStartDate.setDate(cacheStartDate.getDate() - 2);
      const cacheStartDateText = Utility.convertDateToFullDateTime(cacheStartDate);
      cacheEndDate.setDate(cacheEndDate.getDate() + 3);
      const cacheEndDateText = Utility.convertDateToFullDateTime(cacheEndDate);

      const checkDBExist = () => new Promise<boolean>(async (resolveParent, _rejectParent) => {
        const dqp = new Promise<IDBDatabase>(async (resolveChild, rejectChild) => {
          const req = window.indexedDB.open('workTimeDB');
          req.onsuccess = ev => resolveChild((ev.target as IDBOpenDBRequest).result);
          req.onerror = ev => rejectChild((ev.target as IDBOpenDBRequest).error);
        });
        const db = await dqp
        .then()
        .catch(error => {
          console.log(error);
          resolveParent(false);
        });
        if ((db as IDBDatabase).objectStoreNames === undefined)
        {
          resolveParent(false);
        }
        resolveParent((db as IDBDatabase).objectStoreNames.contains('time-stamp'));
      });

      const getLocalData = () => {
        return new Promise<Array<TimeStampRecord>>(async (resolveLocalData, rejectLocalData) => {
          const localRecords = new Array<TimeStampRecord>();
          this.dbService.openCursorByIndex('time-stamp', 'uid', IDBKeyRange.only(user.uid)).subscribe((evt) => {
            const cursor = (<any>evt.target).result;
            if (cursor) {
              const localRecord = Object.assign({}, cursor.value['doc']) as TimeStampRecord;
              localRecord.id = cursor.value['id'];
              // 3ヵ月前から全て取得
              if (localRecord.timeStamp >= cacheStartDateText) {
                localRecords.push(localRecord);
              }
              cursor.continue();
            } else {
              resolveLocalData(localRecords);
            }
          }, (error)  => {
            rejectLocalData(error);
          });
        });
      };
      // IndexedDBのデータを読み出す。
      let localData;
      try {
        const res = await checkDBExist();
        if (!res) {
          console.log('ObjectStore [time-stamp] does not exist. Redefinition ObjectStore');
          // キャッシュクリア等でDB・ストアが無くなっていた場合は新しく定義し直す
          const storeSchema: ObjectStoreMeta = {
            store: 'time-stamp',
            storeConfig: { keyPath: 'id', autoIncrement: false },
            storeSchema: [
              { name: 'uid', keypath: 'uid', options: { unique: false } },
              { name: 'doc', keypath: 'doc', options: { unique: false } }
            ]
          };
          this.dbService.createObjectStore(storeSchema);
          console.log('Finish Redefinition');
        }
        localData = await getLocalData();
      } catch (error) {
        console.log(error);
        return;
      }
      let startDateText = '';
      if (localData === undefined || localData.length === 0 || withoutCache) {
        // IndexedDBにデータが無い初回の読み込みではfirestoreから3ヵ月分読み込む
        startDateText = cacheStartDateText;
      } else {
        // IndexedDBのデータで最遅打刻を開始日として採用する。
        localData.forEach(r => {
          if (!startDateText || startDateText < r['timeStamp']) {
            startDateText = r['timeStamp'];
          }
          // IndexedDBのデータを登録用レコードに追加する。
          records.push(Object.assign({}, r) as TimeStampRecord);
        });
      }

      if (key === undefined || key === '') {
        console.log(`no uid given`);
        resolve(report);
        return;
      }

      // Firestoreから打刻レコードを取得する。
      // 開始は敢えて>にしている。開始日時より大きい条件として、IndexedDBの最終レコードと同じデータを取らないため。
      // 仮にIndexedDBが空だったとしても、開始日の00:00:01～取得するため、問題ない。
      const timeStampCollection = collection(this.firestore, 'time-stamp', 'stamps', key);
      const timeStampQuery = query(
        timeStampCollection,
        where('timeStamp', '>', startDateText),
        where('timeStamp', '<=', cacheEndDateText)
      );
      const timeStampSnapShot = await getDocs(timeStampQuery);

      // 打刻レコードを登録用レコードに追加する。
      await Promise.all(timeStampSnapShot.docs.map(async docSnap => {
        const record = Object.assign({}, docSnap.data()) as TimeStampRecord;
        record.id = docSnap.id;
        records.push(record);
      }));

      // 打刻レコードを時系列でソートする。
      records.sort((a, b) => {
        if (a.timeStamp > b.timeStamp) { return 1; }
        if (a.timeStamp < b.timeStamp) { return -1; }
        if (a.timeStamp === b.timeStamp) { return 0; }
      });

      // レポートオブジェクトに実打刻レコードを登録する。
      records
      .filter(r => r.timeStamp >= registerStartDateText && r.timeStamp <= registerEndDateText)
      .forEach(r => {
          report.register(r.timeStamp, r.kind, r.placeId, r.target);
      });

      // 自身が発行した申請レコードを取得して登録する。
      // 期間とuidで絞り込む読み取り量を抑える
      const changeRequestCollection = collection(this.firestore, 'change-request');
      const requestQuery = query(
        changeRequestCollection,
        where('target', '>=', Utility.convertDateToTimestamp(registerStartDate)),
        where('target', '<=', Utility.convertDateToTimestamp(registerEndDate))
      );
      const requestSnapShot = await getDocs(requestQuery);

      // 旧申請データレコードの混在に対応するため、一度スナップショットをイテレートしてDataVersionを見て変換する。
      const requests = new Array<RequestRecord>();
      const oldRequests = new Array();
      requestSnapShot.forEach(docSnap => {
        const data = docSnap.data() as DocumentData;
        if (data['dataVersion'] === undefined) {
          // uidでフィルタする
          if (data['uid'] === key) {
            oldRequests.push(data);
          }
        } else {
          // 1日単位の新申請レコード
          const request: RequestRecord = Object.assign({}, data) as RequestRecord;
          if (data['uid'] === key) {
            requests.push(request);
          }
        }
      });


      // 同じ対象日の申請が複数ある場合は、最終の申請だけを採用する。
      // ターゲット年月日を、重複を排除して取り出す。
      const dateKeys = Array.from(new Set(requests.map( r => Utility.convertLocalTimeToJstDate(r.target.toDate()).valueOf())));
      const uniqueRequests = new Array();
      // ターゲット年月日ごとに、作成日時が最も新しいものだけを使う。
      dateKeys.forEach( k => {
        const x = requests.filter( rr => Utility.convertLocalTimeToJstDate(rr.target.toDate()).valueOf() === k);
        // console.log(x);
        let max: RequestRecord;
        x.forEach( xx => {
          if (max === undefined || xx.created > max.created) {
            max = xx;
          }
        });
        uniqueRequests.push(max);
      });

      // レポートにリクエストデータを登録する。
      for ( const r of uniqueRequests) {
        // foreachでasyncを付けると、中でawaitしても待たずに処理が進んでしまう。for-ofを使って回避した。
        let name = '未割当';

        if(typeof r.approverUid !== "undefined"){
          const approverSnapshot = await getDoc(doc(this.firestore, 'users', r.approverUid));
          if (approverSnapshot.exists()) {
            name = (approverSnapshot.data()?.['displayName'] as string) ?? name;
          }
        }
        report.registerRequest(r, name);
      }

      if (!withoutCache) {
        // IndexedDBに3ヵ月分のデータを上書きする
        console.log('update IndexedDB start');
        // 全キャッシュデータ削除
        // this.db.clear('time-stamp')でクリアしたいが、部署別の給与計算レポート作成時に複数人のレコードを読み込むため、uidが一致するもののみ削除する
        const clearByUid = () => {
          return new Promise<Array<TimeStampRecord>>(async (resolveLocalData, _rejectLocalData) => {
            const localRecords = new Array<TimeStampRecord>();
            this.dbService.openCursorByIndex('time-stamp', 'uid', IDBKeyRange.only(user.uid)).subscribe((evt) => {
              const cursor = (<any>evt.target).result;
              if (cursor) {
                this.dbService.delete('time-stamp', cursor.value['id']).subscribe({
                  error: err => console.error(err)
                });
                cursor.continue();
              } else {
                resolveLocalData(localRecords);
              }
            });
          });
        };
        clearByUid().then(async () => {
          // 3ヵ月分を書き込み
          Promise.all(records.map(record =>
            firstValueFrom(this.dbService.add(`time-stamp`, { id: record.id, uid: user.uid, doc: record }))
          )).then(() => {
            console.log('update IndexedDB fin');
          });
        });
      }

      // dataVersion無しのデータは既に表示期間外なので、無効化する。様子見してOKなら完全削除する。
      // // 旧データを登録する。-->
      // const convertedRequest = this.convertOldRequest(oldRequests, report);
      // if (convertedRequest !== undefined) {
      //   convertedRequest.forEach( r => {
      //     report.registerRequest(r);
      //   });
      // }
      // // <--

      // console.log('req register ');
      // console.log(report);

      resolve(report);
    });
  }



  // dataVersion無しのデータは既に表示期間外なので、無効化する。様子見してOKなら完全削除する。
  // convertOldRequest(oldRequests, report: Report): Array<RequestRecord> {
  //   // 仕切り直し。
  //   // 実打刻と、申請データは全部出揃って1日単位で判断しないと無理。

  //   if (oldRequests.length <= 0) {
  //     return undefined;
  //   }
  //   // 旧申請データレコードを1日単位にまとめる。
  //   const requests = new Array();
  //   oldRequests.forEach( doc => {
  //     const uid = doc['uid'];
  //     const timeStamp = Utility.createDateFromText(doc['timeStamp']);
  //     const state = doc['state'];
  //     const cause = doc['cause'];
  //     const kind = doc['kind'];
  //     const approverUid = doc['approverUid'];

  //     // 却下されたものは飛ばす。
  //     if (state === Define.RequestState.Reject) {
  //       return;
  //     }

  //     // 対象営業日を判定する。一旦、AM5時を境界としておく。
  //     const targetDate = Utility.getBusinessDate(kind, timeStamp);
  //     const tmp = requests.find( elm => Utility.compareDay(elm.target, targetDate));
  //     if (tmp === undefined) {
  //       const t =  { target: targetDate, actualStamps: new Array<Date>(), requests: new Array()};
  //       t.requests.push({uid: uid, timeStamp: timeStamp, state: state, kind: kind, cause: cause, approverUid: approverUid});
  //       requests.push(t);
  //     } else {
  //       requests[requests.indexOf(tmp)].requests.push(
  //         {uid: uid, timeStamp: timeStamp, state: state, kind: kind, cause: cause, approverUid: approverUid});
  //     }
  //   });
  //   requests.forEach( request => {
  //     // 実打刻データを1日単位でまとめる。
  //     const actualData = report.data.find( elm => Utility.compareDay(elm.date, request.target));
  //     console.log(actualData);
  //     if (actualData !== undefined) {
  //       const target = requests.find( r => r.target === request.target);
  //       // 実打刻データを申請データへとりあえず丸ごと登録。
  //       actualData.sourceStamps.forEach( ss => {
  //         target.actualStamps.push(ss);
  //       });
  //     }
  //   });
  //   const convertedRequests = new Array<RequestRecord>();
  //   requests.forEach( request => {
  //     // 1日分の実打刻と申請データを重ね合わせる。
  //     // 先に申請データを置いて、隙間を実打刻データで埋める。
  //     let arriveReq: Date;
  //     let leaveReq: Date;
  //     let temporaryLeaveReq: Date;
  //     let reenterReq: Date;
  //     let isCancelTemporaryLeave = false;
  //     let isCancelReenter = false;
  //     let isApproved = true;
  //     const oneDayReq = new Array<firestore.Timestamp>();
  //     request.requests.forEach(element => {
  //       switch (element.kind) {
  //         case Define.RequestKindDef.Arrive:
  //           if (arriveReq !== undefined) {
  //             if ( arriveReq > element.timeStamp ) {
  //               arriveReq = element.timeStamp;
  //             }
  //           } else {
  //             arriveReq = element.timeStamp;
  //           }
  //           break;
  //         case Define.RequestKindDef.Leave:
  //           if (leaveReq !== undefined) {
  //             if ( leaveReq < element.timeStamp ) {
  //               leaveReq = element.timeStamp;
  //             }
  //           } else {
  //             leaveReq = element.timeStamp;
  //           }
  //           break;
  //         case Define.RequestKindDef.TemporaryLeave:
  //           if (temporaryLeaveReq !== undefined) {
  //             if ( temporaryLeaveReq > element.timeStamp ) {
  //               temporaryLeaveReq = element.timeStamp;
  //             }
  //           } else {
  //             temporaryLeaveReq = element.timeStamp;
  //           }
  //           break;
  //         case Define.RequestKindDef.Reenter:
  //           if (reenterReq !== undefined) {
  //             if ( reenterReq > element.timeStamp ) {
  //               reenterReq = element.timeStamp;
  //             }
  //           } else {
  //             reenterReq = element.timeStamp;
  //           }
  //           break;
  //         case  Define.RequestKindDef.CancelTemporaryLeave:
  //           isCancelTemporaryLeave = true;
  //           break;
  //         case  Define.RequestKindDef.CancelReenter:
  //           isCancelReenter = true;
  //           break;
  //       }
  //       if ( element.state !== Define.RequestState.Approve) {
  //         isApproved = false;
  //       }
  //     });
  //     // フラグによってどれを使うか決定する。
  //     const actualCount = request.actualStamps.length;
  //     if (actualCount <= 2) {
  //       // 打刻が2回以下なら、申請データと実打刻を全て採用する。
  //       request.actualStamps.forEach( element => oneDayReq.push(Utility.convertDateToTimestamp(element)));
  //       request.requests.forEach( element => oneDayReq.push(Utility.convertDateToTimestamp(element.timeStamp)));

  //     } else {
  //       // 打刻が3回以上の場合、上書きするのか追加するのかを判断する。
  //       if (arriveReq !== undefined ) {
  //         oneDayReq.push(Utility.convertDateToTimestamp(arriveReq));
  //       } else {
  //         oneDayReq.push(Utility.convertDateToTimestamp(request.actualStamps[0]));
  //       }
  //       if (leaveReq !== undefined ) {
  //         oneDayReq.push(Utility.convertDateToTimestamp(leaveReq));
  //       } else {
  //         oneDayReq.push(Utility.convertDateToTimestamp(request.actualStamps[actualCount - 1]));
  //       }
  //       // 外出取消有りなら、[1]は使わない。
  //       // 外出有りなら、[1]は使わない。申請データは登録する。
  //       if (isCancelTemporaryLeave === false && temporaryLeaveReq !== undefined) {
  //         oneDayReq.push(Utility.convertDateToTimestamp(request.actualStamps[1]));
  //       }
  //       if (temporaryLeaveReq !== undefined) {
  //         oneDayReq.push(Utility.convertDateToTimestamp(temporaryLeaveReq));
  //       }
  //       // 戻り取消有りなら、[2]もしくはlength - 2の大きい方は使わない。
  //       // 戻り有りなら、[2]もしくはlength - 2の大きい方は使わない。申請データは登録する。
  //       if (isCancelReenter === false && reenterReq !== undefined) {
  //         const index = 2 < actualCount - 2 ? 2 : actualCount - 2;
  //         oneDayReq.push(Utility.convertDateToTimestamp(request.actualStamps[index]));
  //       }
  //       if (reenterReq !== undefined) {
  //         oneDayReq.push(Utility.convertDateToTimestamp(temporaryLeaveReq));
  //       }
  //     }
  //     const state = isApproved ? Define.RequestState.Approve : Define.RequestState.Registered;
  //     // 時系列でソートする。
  //     oneDayReq.sort( (a, b) => {
  //       if (a > b) {
  //         return 1;
  //       }
  //       if (a < b) {
  //         return -1;
  //       }
  //       return 0;
  //     });
  //     const newRequest = new RequestRecord(
  //       0,
  //       new firestore.Timestamp(Math.floor(new Date().getTime() / 1000), 0),
  //       Utility.convertDateToTimestamp(request.target),
  //       request.uid, oneDayReq, state, '旧型式の申請変換', request.approverUid);
  //     convertedRequests.push(newRequest);
  //   });
  //   console.log('convertedRequests');
  //   console.log(convertedRequests);
  //   return convertedRequests;
  // }
}
